// Base Analytics Provider
//
// This module provides the base interface and abstract implementation for analytics providers.
// All analytics providers (PostHog, N8N, Database, etc.) should implement this interface.

import { getConsentMode } from '../../services/consentMode';
import { logger } from '../../utils/core/logger';
import { canExportAnalytics, getAnalyticsConsentGeneration } from '../consent';
import type { AnalyticsContext, AnalyticsEvent, AnalyticsProvider } from '../types';

/**
 * Abstract base class for analytics providers
 * Provides common functionality and error handling
 */
export abstract class BaseAnalyticsProvider implements AnalyticsProvider {
    public abstract readonly name: string;

    protected context: AnalyticsContext = {};
    protected enabled: boolean = true;
    protected onContextUpdate?: (context: Partial<AnalyticsContext>) => void;

    public abstract eventFilter(event: AnalyticsEvent): boolean;
    protected abstract sendEvent(event: AnalyticsEvent): Promise<void>;

    public async track(event: AnalyticsEvent): Promise<void> {
        const capturedEvent: AnalyticsEvent = {
            ...event,
            consentMode: event.consentMode ?? getConsentMode(),
            consentGeneration: event.consentGeneration ?? getAnalyticsConsentGeneration(),
        };
        if (this.name !== 'posthog' && !canExportAnalytics(capturedEvent)) return;
        if (!this.isEnabled()) {
            logger.debug(`Provider ${this.name} is disabled, skipping event:`, event.type);
            return;
        }

        if (!this.eventFilter(capturedEvent)) {
            logger.debug(`Provider ${this.name} filtered out event:`, event.type);
            return;
        }

        try {
            await this.sendEvent(capturedEvent);
        } catch (error) {
            logger.error(`Provider ${this.name} failed to track event ${event.type}:`, error);
            throw error; // Re-throw to allow event bus to handle the error
        }
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        logger.debug(`Provider ${this.name} ${enabled ? 'enabled' : 'disabled'}`);
    }

    public async initialize(context: AnalyticsContext): Promise<void> {
        this.context = { ...context };
        logger.debug(`Provider ${this.name} initialized with context:`, context);
    }

    public updateContext(context: Partial<AnalyticsContext>): void {
        this.context = { ...this.context, ...context };
        logger.debug(`Provider ${this.name} context updated:`, context);
    }

    public async destroy(): Promise<void> {
        logger.debug(`Provider ${this.name} destroyed`);
    }

    protected getContext(): AnalyticsContext {
        return { ...this.context };
    }

    /**
     * Set callback for context updates
     * Allows providers to notify the event bus of context changes (e.g., when personId becomes available)
     */
    public setContextUpdateCallback(callback: (context: Partial<AnalyticsContext>) => void): void {
        this.onContextUpdate = callback;
    }

    /**
     * Notify event bus of context updates
     */
    protected notifyContextUpdate(context: Partial<AnalyticsContext>): void {
        if (this.onContextUpdate) {
            this.onContextUpdate(context);
        }
    }
}

/**
 * Simple provider implementation for providers that don't need complex logic
 */
export abstract class SimpleAnalyticsProvider extends BaseAnalyticsProvider {
    protected timeoutMs: number;

    constructor(timeoutMs: number = 5000) {
        super();
        this.timeoutMs = timeoutMs;
    }

    protected async sendEvent(event: AnalyticsEvent): Promise<void> {
        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error(`Provider ${this.name} timeout`)), this.timeoutMs);
        });

        try {
            await Promise.race([this.sendEventImpl(event), timeoutPromise]);
        } catch (error) {
            if (error instanceof Error && error.message.includes('timeout')) {
                logger.warn(`Provider ${this.name} timed out sending event ${event.type}`);
            }
            throw error;
        }
    }

    protected abstract sendEventImpl(event: AnalyticsEvent): Promise<void>;
}
